import React, { useState } from "react";
import {
  Box,
  Typography,
  Checkbox,
  TextField,
  Autocomplete,
  Chip,
  Link,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  DataGridPro as DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro";
import { Assembly, SiteMetadata, TimeSeriesMetric } from "../../lib/Types";
import { uuidsMatch } from "../Graphs/Logic/formatUtils";

/** Remove metrics that don't have units or valid value types */
const getProcessedAssemblies = (site: SiteMetadata): Assembly[] => {
  const processedAssemblies = site.assemblies.map((assembly: Assembly) => {
    const validMetrics = assembly.time_series.filter(
      (metric: TimeSeriesMetric) =>
        metric.unit?.trim() ||
        (metric.value_type && ["DOUBLE PRECISION", "INTEGER"].includes(metric.value_type)),
    );
    assembly.time_series = validMetrics;
    return assembly;
  });
  return processedAssemblies;
};

interface Props {
  site: SiteMetadata;
  updateSelectedMetrics: (metrics: string[]) => void;
  selectedMetricUuids: string[];
}

interface MetricRow {
  id: string;
  source: string;
  assetType: string;
  asset: string;
  metric: string;
  uuid: string;
}

const SiteEquipmentList: React.FC<Props> = ({
  site,
  updateSelectedMetrics,
  selectedMetricUuids,
}) => {
  const assemblies = getProcessedAssemblies(site) || [];

  // Get unique values for filters

  const uniqueSources = (() => {
    const sources = new Set<string>();
    assemblies.forEach((assembly) => {
      assembly.source_info.forEach((sourceInfo) => {
        sources.add(sourceInfo.source);
      });
    });
    return Array.from(sources)
      .filter((type) => type !== null)
      .sort();
  })();

  const getDefaultSources = () => {
    if (uniqueSources.includes("edge")) {
      return ["edge"];
    }
    return [];
  };

  // Unified filter state for progressive options
  type EquipmentFilterOptions = {
    source: Set<string>;
    assetType: Set<string>;
    asset: Set<string>;
  };

  const defaultEquipmentFilter: EquipmentFilterOptions = {
    source: new Set(getDefaultSources()),
    assetType: new Set(),
    asset: new Set(),
  };

  const [appliedFilter, setAppliedFilter] =
    useState<EquipmentFilterOptions>(defaultEquipmentFilter);

  const metricRows = (() => {
    const rows: MetricRow[] = [];
    assemblies.forEach((assembly) => {
      assembly.time_series.forEach((metric) => {
        assembly.source_info.forEach((sourceInfo) => {
          rows.push({
            id: `${assembly.uuid}-${metric.uuid}-${sourceInfo.source}`,
            source: sourceInfo.source,
            assetType: assembly.scaleos_type || "Other",
            asset: assembly.scaleos_name,
            metric: metric.name,
            uuid: metric.uuid,
          });
        });
      });
    });
    return rows;
  })();

  // Filtering utilities
  const valueGetters = {
    source: (row: MetricRow) => row.source,
    assetType: (row: MetricRow) => row.assetType,
    asset: (row: MetricRow) => row.asset,
  } as const;

  const getOptions = (rows: MetricRow[], field: keyof EquipmentFilterOptions): Set<string> => {
    const set = new Set<string>();
    rows.forEach((r) => {
      const v = valueGetters[field](r);
      if (v) set.add(v);
    });
    return set;
  };

  const getFilteredRowsByFilter = (
    rows: MetricRow[],
    filter: EquipmentFilterOptions,
  ): MetricRow[] => {
    return rows.filter((r) => {
      const sourceOk = filter.source.size === 0 || filter.source.has(r.source);
      const assetTypeOk = filter.assetType.size === 0 || filter.assetType.has(r.assetType);
      const assetOk = filter.asset.size === 0 || filter.asset.has(r.asset);
      return sourceOk && assetTypeOk && assetOk;
    });
  };

  const getFilteredOptions = (
    rows: MetricRow[],
    filter: EquipmentFilterOptions,
    excludedField: keyof EquipmentFilterOptions,
  ): Set<string> => {
    const f: EquipmentFilterOptions = {
      source: new Set(filter.source),
      assetType: new Set(filter.assetType),
      asset: new Set(filter.asset),
    };
    f[excludedField] = new Set();
    const partiallyFiltered = getFilteredRowsByFilter(rows, f);
    return getOptions(partiallyFiltered, excludedField);
  };

  const filteredRows = React.useMemo(() => {
    return getFilteredRowsByFilter(metricRows, appliedFilter);
  }, [metricRows, appliedFilter]);

  // Build "<asset name> - <metric>" labels for each selected metric uuid,
  // preserving the order in which they were selected.
  const selectedMetricLabels = React.useMemo(() => {
    const labelByUuid = new Map<string, string>();
    metricRows.forEach((row) => {
      const key = row.uuid.replace(/-/g, "").toLowerCase();
      if (!labelByUuid.has(key)) {
        labelByUuid.set(key, `${row.asset} - ${row.metric}`);
      }
    });
    return selectedMetricUuids
      .map((uuid) => labelByUuid.get(uuid.replace(/-/g, "").toLowerCase()))
      .filter((label): label is string => Boolean(label));
  }, [metricRows, selectedMetricUuids]);

  const handleChange = (field: keyof EquipmentFilterOptions) => (_: any, value: string[]) => {
    setAppliedFilter((prev) => {
      const next: EquipmentFilterOptions = {
        source: new Set(prev.source),
        assetType: new Set(prev.assetType),
        asset: new Set(prev.asset),
      };
      next[field] = new Set(value);
      return next;
    });
  };

  const getSelector = (label: string, field: keyof EquipmentFilterOptions) => {
    const options = Array.from(getFilteredOptions(metricRows, appliedFilter, field)).sort((a, b) =>
      a.localeCompare(b),
    );
    const selected = Array.from(appliedFilter[field]);
    return (
      <Autocomplete
        multiple
        options={options}
        value={selected}
        onChange={handleChange(field)}
        renderInput={(params) => <TextField {...params} label={label} size="small" />}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
          ))
        }
        sx={{ minWidth: 200 }}
      />
    );
  };

  const handleMetricSelection = (metricUuid: string, checked: boolean) => {
    if (checked) {
      const alreadySelected = selectedMetricUuids.some((item) => uuidsMatch(item, metricUuid));
      if (alreadySelected) return;
      updateSelectedMetrics([...selectedMetricUuids, metricUuid]);
    } else {
      updateSelectedMetrics(selectedMetricUuids.filter((item) => !uuidsMatch(item, metricUuid)));
    }
  };

  const clearMetrics = () => {
    updateSelectedMetrics([]);
  };

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <Stack direction="row" spacing={2} alignItems="start" sx={{ width: "100%", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexGrow: 1 }}>
          {getSelector("Sources", "source")}
          {getSelector("Asset Type", "assetType")}
          {getSelector("Asset", "asset")}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <GridToolbarQuickFilter placeholder="Search all" />
        </Box>
      </Stack>
    </GridToolbarContainer>
  );

  const columns: GridColDef[] = [
    {
      field: "selection",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      cellClassName: "checkbox-cell",
      renderCell: (params) => (
        <Checkbox
          checked={selectedMetricUuids.some((uuid) => uuidsMatch(uuid, params.row.uuid))}
          onChange={(e) => handleMetricSelection(params.row.uuid, e.target.checked)}
          size="small"
        />
      ),
    },
    {
      field: "source",
      headerName: "Source",
      width: 120,
      filterable: true,
    },
    {
      field: "assetType",
      headerName: "Asset Type",
      width: 150,
      filterable: true,
    },
    {
      field: "asset",
      headerName: "Asset",
      width: 200,
      filterable: true,
    },
    {
      field: "metric",
      headerName: "Metric",
      width: 400,
      filterable: true,
    },
  ];

  const styles = {
    datagrid: {
      border: "none",
      "& .MuiDataGrid-root": {
        border: "none",
        outline: "none",
      },
      "& .MuiDataGrid-main": {
        border: "none",
        outline: "none",
      },
      "& .MuiDataGrid-virtualScroller": {
        border: "none",
        outline: "none",
      },
      "& .MuiDataGrid-virtualScrollerContent": {
        border: "none",
        outline: "none",
      },
      "& .MuiDataGrid-virtualScrollerRenderZone": {
        border: "none",
        outline: "none",
      },
      "& .MuiDataGrid-footerContainer": {
        border: "none",
        borderTop: "none",
        borderBottom: "none",
      },
      "& .MuiDataGrid-columnHeaders": {
        borderBottom: "none",
      },
      "& .MuiDataGrid-cell": {
        borderBottom: "none",
      },
      "& .MuiDataGrid-row": {
        borderBottom: "none",
      },
      "& .checkbox-cell": {
        overflow: "hidden",
        textOverflow: "clip",
        whiteSpace: "nowrap",
      },
    },
  };

  return (
    <Box sx={{ width: "100%", height: 600, pb: 2, pl: 4, pr: 2 }}>
      {/* Metrics header with chip and link in same row */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
          Metrics
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Tooltip
            title={
              selectedMetricLabels.length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    fontSize: "10px",
                    lineHeight: "14px",
                    fontWeight: 500,
                    letterSpacing: 0,
                  }}
                >
                  {selectedMetricLabels.map((label) => (
                    <span key={label}>{label}</span>
                  ))}
                </Box>
              ) : (
                ""
              )
            }
            disableHoverListener={selectedMetricLabels.length === 0}
            disableFocusListener={selectedMetricLabels.length === 0}
            disableTouchListener={selectedMetricLabels.length === 0}
            placement="bottom"
            arrow={false}
          >
            <Chip label={`${selectedMetricUuids.length} selected`} variant="filled" size="small" />
          </Tooltip>
          <Link
            component="button"
            variant="body2"
            onClick={clearMetrics}
            sx={{ textDecoration: "none" }}
          >
            Deselect All Metrics
          </Link>
        </Box>
      </Box>

      <DataGrid
        rows={filteredRows}
        columns={columns}
        checkboxSelection={false}
        disableRowSelectionOnClick
        hideFooterRowCount={true}
        density="compact"
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
        sx={styles.datagrid}
      />
    </Box>
  );
};

export default SiteEquipmentList;
